import type { InterstateOnChangeEvent } from "@dealer-center/core-components";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  useToaster
} from "@dealer-center/core-components";
import styled from "styled-components";
import {
  PagesLandingPageListItem,
  StyledUnorderedList
} from "@dealer-center/ddc-core-custom-components";
import { useEffect, useState } from "react";
import type { AccountMapping } from "@dealer-center/ddc-pages-service-account-mappings-interface";
import {
  useServiceAccountMappingsGet,
  useServiceAccountMappingsUpdate
} from "@dealer-center/ddc-pages-service-account-mappings-fetch";
import {
  LoadingIndicator,
  SaveAndCancel
} from "@dealer-center/core-custom-components";
import { useGetUser } from "@dealer-center/ddc-api-user-fetch";
import { useGetAccount } from "@dealer-center/ddc-api-account-fetch";

const StyledCard = styled(Card)`
  .panel-title {
    width: 100%;
  }
`;

export interface pagesLandingPageListItemProps {
  label?: string;
  value?: string | null | undefined | number | boolean | React.ReactElement;
  type?: "externalLink" | "externalLinkHidden";
}

export function DashboardAccountMappingsMain() {
  const { data: userEntity } = useGetUser();
  const enableSave = userEntity?.userPermissions?.SERVICE_ACCOUNT_MAPPINGS;
  const { data: accountEntity } = useGetAccount();
  const { toastError, toastSuccess } = useToaster();
  const {
    data: accountMappings,
    error: fetchError,
    isLoading
  } = useServiceAccountMappingsGet({
    accountEntity
  });
  const {
    mutate,
    error: updateError,
    isSuccess: updateSuccess,
    isPending: isSaving,
    data: updateData
  } = useServiceAccountMappingsUpdate();
  const saveAccountMappings = (newAccountMappings: AccountMapping) => {
    mutate(newAccountMappings);
  };
  const [editMode, setEditMode] = useState(false);
  const [newAccountMappings, setNewAccountMappings] =
    useState<AccountMapping>();
  useEffect(() => {
    if (updateData) {
      setNewAccountMappings(updateData);
    } else if (accountMappings) {
      setNewAccountMappings(accountMappings);
    }
  }, [accountMappings, updateData]);
  useEffect(() => {
    if (updateError) {
      setEditMode(false);
      toastError("Error saving Account Mappings");
    }
  }, [updateError]);
  useEffect(() => {
    if (updateSuccess) {
      setEditMode(false);
      toastSuccess("Saved", "Account Mappings saved successfully");
    }
  }, [updateSuccess]);

  if (isLoading) {
    return <LoadingIndicator actionName="Loading Account Mappings" />;
  }
  if (fetchError) {
    return (
      <div id="PagesServiceAccountMappingsUiError">
        Error loading Account Mappings
      </div>
    );
  }

  if (!newAccountMappings) {
    return <div>No Account Mappings found</div>;
  }

  const cancelClicked = () => {
    setNewAccountMappings(accountMappings);
    setEditMode(false);
  };
  const onSaveClicked = () => {
    saveAccountMappings(newAccountMappings);
  };
  const onChange = (e: InterstateOnChangeEvent<string>) => {
    setNewAccountMappings({
      ...newAccountMappings,
      [e.target.name as keyof AccountMapping]: e.target.value
    });
  };
  return (
    <StyledCard
      content={
        <CardContent>
          <StyledUnorderedList equalLabelWidth>
            <PagesLandingPageListItem
              label="Service Account Id"
              value={newAccountMappings.serviceAccountId}
            />
            <PagesLandingPageListItem
              inputProps={{ maxLength: 25 }}
              label="SVOC"
              name="singleViewOfCustomer"
              type={editMode ? "editableText" : "eventuallyEditable"}
              value={newAccountMappings.singleViewOfCustomer}
              onChange={onChange}
            />
            <PagesLandingPageListItem
              label="Fusion Code"
              name="fusionCode"
              type={editMode ? "editableNumber" : "eventuallyEditable"}
              value={newAccountMappings.fusionCode}
              onChange={onChange}
            />
            <PagesLandingPageListItem
              inputProps={{ maxLength: 20 }}
              label="Infused Fusion Code"
              name="infusedFusionCode"
              type={editMode ? "editableText" : "eventuallyEditable"}
              value={newAccountMappings.infusedFusionCode}
              onChange={onChange}
            />
            <PagesLandingPageListItem
              inputProps={{ maxLength: 20 }}
              label="Dealertrack Id"
              name="dealertrackId"
              type={editMode ? "editableText" : "eventuallyEditable"}
              value={newAccountMappings.dealertrackId}
              onChange={onChange}
            />
            <PagesLandingPageListItem
              inputProps={{ maxLength: 20 }}
              label="ERP Customer Id"
              name="erpCustomerId"
              type={editMode ? "editableText" : "eventuallyEditable"}
              value={newAccountMappings.erpCustomerId}
              onChange={onChange}
            />
            <PagesLandingPageListItem
              inputProps={{ maxLength: 20 }}
              label="CRM Company Id"
              name="crmCompanyId"
              type={editMode ? "editableText" : "eventuallyEditable"}
              value={newAccountMappings.crmCompanyId}
              onChange={onChange}
            />
            <PagesLandingPageListItem
              label="Price List"
              name="priceList"
              type={editMode ? "editableText" : "eventuallyEditable"}
              value={newAccountMappings.priceList}
              onChange={onChange}
            />
            <PagesLandingPageListItem
              inputProps={{ maxLength: 255 }}
              label="CoxAutomotive Id"
              name="coxAutomotiveId"
              type={editMode ? "editableText" : "eventuallyEditable"}
              value={newAccountMappings.coxAutomotiveId}
              onChange={onChange}
            />
            {newAccountMappings.audit && (
              <>
                <PagesLandingPageListItem
                  label="Audit Created"
                  value={newAccountMappings.audit.created}
                />
                <PagesLandingPageListItem
                  label="Audit Created By"
                  value={newAccountMappings.audit.createdBy}
                />
                <PagesLandingPageListItem
                  label="Audit Last Modified"
                  value={newAccountMappings.audit.lastModified}
                />
                <PagesLandingPageListItem
                  label="Audit Last Modified By"
                  value={newAccountMappings.audit.lastModifiedBy}
                />
              </>
            )}
          </StyledUnorderedList>
        </CardContent>
      }
      header={
        <CardHeader
          action={
            !enableSave ? null : !editMode ? (
              <Button
                analyticsConfiguration={{
                  eventName: "Account Mappings Edit"
                }}
                onClick={() => setEditMode(true)}
              >
                Edit
              </Button>
            ) : (
              <SaveAndCancel
                analyticsEventName="Account Mappings"
                isSaving={isSaving}
                onCancel={cancelClicked}
                onSave={onSaveClicked}
              />
            )
          }
          title="Account Mappings"
        />
      }
    />
  );
}

export default DashboardAccountMappingsMain;
